import type { IAdViewImage } from "@finn-no/adview-provider-frontend-types";
import { AdPlacement, AdvertisingClientWebComponent, useIsBannerLoaded } from "@schibsted-nmp/advertising-companion";
import { events, messageBus } from "@schibsted-nmp/advertising-events";
import { useEffect, useRef } from "react";

type AdPlacementType = typeof AdPlacement;

type AdvertisingContainerProps = {
  placement: AdPlacementType[keyof AdPlacementType];
  className?: string;
};

export const FinancingAdvertisingPlacement1 = () => <FinancingPlacement placement={AdPlacement.AdvtFinancing1} />;

export const MobileOnlyFinancingAdvertisingPlacements = () => (
  <>
    <FinancingPlacement placement={AdPlacement.AdvtFinancing2} />
    <FinancingPlacement placement={AdPlacement.AdvtFinancing3} classNameOverride="mt-20" />
  </>
);

const FinancingPlacement = ({
  placement,
  classNameOverride,
}: {
  placement: AdPlacementType[keyof AdPlacementType];
  classNameOverride?: string;
}) => {
  const isLoaded = useIsBannerLoaded(placement.id);
  const className = isLoaded ? (classNameOverride ?? "border-t pt-40 mt-40") : "";
  return <AdvertisingContainer className={className} placement={placement} />;
};

export const AdvertisingContainer = ({ placement, className = "" }: AdvertisingContainerProps) => {
  return (
    <div className={className}>
      <AdvertisingClientWebComponent placementId={placement.id} />
    </div>
  );
};

type Props = {
  images: IAdViewImage[];
  currentImageIndex: number;
  onLoaded: (loaded: boolean) => void;
};

export const InCarouselAdvertisingPlacement = ({ images, currentImageIndex, onLoaded }: Props) => {
  const localGuard = useRef(false);
  const isLoaded = useIsBannerLoaded(AdPlacement.AdvtIncarousel.id);

  useEffect(() => {
    onLoaded(!!isLoaded);
  }, [isLoaded, onLoaded]);

  useEffect(() => {
    if (localGuard.current) return;

    const imageUri = images[currentImageIndex]?.uri ?? images[0]?.uri;
    if (!imageUri) return;

    messageBus.publish(
      events.ITEM_PAGE.channel,
      events.ITEM_PAGE.INCAROUSEL_IMAGE_UPDATE.topic,
      events.ITEM_PAGE.INCAROUSEL_IMAGE_UPDATE.sendPayload({ imageUri }),
    );

    localGuard.current = true;
  }, [images, currentImageIndex]);

  // Mount HTML to request in-carousel ad, then hide if it fails to load
  return <AdvertisingClientWebComponent placementId={AdPlacement.AdvtIncarousel.id} />;
};
